import * as React from 'react'

import { DEFAULT_RECT, getBoundingRect, Rect } from '../utils/dom'

interface BoundingRectConfig {
  dependencies?: Array<any>
}

interface Props extends BoundingRectConfig {
  children: React.ReactNode
}

interface RenderProps extends BoundingRectConfig {
  children: (props: BoundingRectApi) => {}
}

interface BoundingRectApi {
  ref: React.RefObject<any>
  rect: Rect
}

/* HOOK */

export const useBoundingRect: (
  config: BoundingRectConfig
) => BoundingRectApi = ({ dependencies = [] }: BoundingRectConfig = {}) => {
  const ref = React.useRef<any>(null)
  const [rect, setRect] = React.useState(DEFAULT_RECT)

  React.useLayoutEffect(() => {
    if (ref.current) {
      setRect(getBoundingRect(ref.current))
    }
  }, [ref.current, ...dependencies])

  return { ref, rect }
}

/* DECORATOR */

export const withBoundingRect = <T extends {}>(config: BoundingRectConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { boundingRect: BoundingRectConfig }) => (
  <Component
    {...props}
    boundingRect={useBoundingRect({ ...config, ...props.boundingRect })}
  />
)

/* RENDER PROP */

export const BoundingRect = ({ children, ...props }: RenderProps) => {
  const boundingRect = useBoundingRect(props)

  return typeof children === 'function' ? children(boundingRect) : children
}

/* CONTEXT */

const context: BoundingRectApi = {
  ref: { current: null },
  rect: DEFAULT_RECT,
}

export const BoundingRectContext = React.createContext(context)

export const BoundingRectProvider = ({ children, ...props }: Props) => (
  <BoundingRectContext.Provider value={useBoundingRect(props)}>
    {children}
  </BoundingRectContext.Provider>
)

export const consumeBoundingRect = () => React.useContext(BoundingRectContext)
